import React, { useRef, useState } from "react";
import { Volume2, VolumeX, Maximize2 } from "lucide-react";

/**
 * Full-bleed cinematic video reel with Vimeo autoplay (muted + loop).
 * Presents a video "on rails" between sections. Includes unmute + fullscreen controls.
 */
export default function CinematicReel({
  vimeoId,
  eyebrow = "Reel",
  title,
  subtitle,
}) {
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    // Reload iframe with new muted param to toggle audio
    setMuted((m) => {
      const next = !m;
      if (iframeRef.current) {
        iframeRef.current.src = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&loop=1&muted=${next ? 1 : 0}&background=0&byline=0&title=0&portrait=0&controls=0`;
      }
      return next;
    });
  };

  const openFullscreen = () => {
    const el = iframeRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  return (
    <section className="relative bg-black pt-2 pb-12 md:pt-4 md:pb-20 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        {(eyebrow || title || subtitle) && (
          <div className="mb-6 md:mb-8 flex items-end justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              {eyebrow && (
                <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-[#ff8a3c]">— {eyebrow} —</p>
              )}
              {title && (
                <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] pt-1">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-3 text-white/60 text-base md:text-lg max-w-2xl">{subtitle}</p>
              )}
            </div>
          </div>
        )}

        <div className="relative group rounded-2xl md:rounded-3xl overflow-hidden border border-white/10 bg-black shadow-2xl">
          {/* Aspect box */}
          <div className="relative w-full aspect-video">
            <iframe
              ref={iframeRef}
              src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&loop=1&muted=1&background=0&byline=1&title=1&portrait=0&controls=1`}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Kilombo Music Reel"
            />
            {/* Subtle vignette on hover */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-70" />
          </div>

          {/* Floating controls */}
          <div className="absolute bottom-4 right-4 md:bottom-5 md:right-5 flex items-center gap-2">
            <button
              onClick={toggleMute}
              aria-label={muted ? "Activar sonido" : "Silenciar"}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur border border-white/20 hover:border-[#ff8a3c] text-white/90 hover:text-[#ff8a3c] font-mono-caps text-xs tracking-[0.2em] uppercase transition-colors"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              <span className="hidden md:inline">{muted ? "Activar sonido" : "Silenciar"}</span>
            </button>
            <button
              onClick={openFullscreen}
              aria-label="Pantalla completa"
              className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur border border-white/20 hover:border-white text-white/90 hover:text-white transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
